<template>
  <div class="container">
    <h1 class="title">{{ $t("user.profile.title") }}</h1>
    <h2 class="subtitle">
      {{ $t("user.profile.description") }}
    </h2>
    <section class="section is-main-section" v-if="isLoadingAuth">
      <b-loading :is-full-page="false" :active="true">
        <b-icon
          pack="fas"
          icon="sync-alt"
          size="is-small"
          custom-class="fa-spin"
        >
        </b-icon>
      </b-loading>
    </section>
    <b-notification
      v-if="isUserAuth && !user.emailVerified"
      type="is-warning is-light"
      aria-close-label="Close notification"
      role="alert"
    >
      {{ $t("user.profile.notVerifiedWarning") }}
      <p class="mt-5" v-if="!verificationSent && profile.email == user.email">
        <a
          href="#"
          ref="verificationButton"
          @click="requestEmailVerification"
          >{{ $t("user.profile.verifyEmail") }}</a
        >
      </p>
      <p
        class="mt-5"
        v-else-if="this.verificationSent && profile.email == user.email"
      >
        <strong>{{ $t("user.profile.checkYourEmail") }}</strong>
        <br />{{ $t("user.profile.checkYourEmailSpam") }}
      </p>
    </b-notification>

    <section class="is-main-section" v-if="isUserAuth">
      <div class="prospect columns is-multiline">
        <div class="column">
          <header>
            <div
              class="is-divider"
              :data-content="$t('user.profile.section.profile')"
            ></div>
          </header>
          <div class="card-content">
            <ValidationObserver ref="observer" v-slot="{ handleSubmit, valid }">
              <b-field
                class="mt-5"
                :label="$t('user.profile.firstAndLastName')"
              >
                <b-input
                  tabindex="1"
                  type="text"
                  autocomplete="on"
                  name="displayName"
                  v-model="profile.displayName"
                />
              </b-field>

              <b-field class="mt-5" :label="$t('user.profile.address')">
                <b-autocomplete
                  expanded
                  v-model="profile.address1"
                  tabindex="3"
                  :data="addressResults"
                  :placeholder="$t('user.profile.address1')"
                  field="adresse"
                  :loading="isFetchingAddress"
                  @typing="getAddressSuggestion"
                  @select="updateAddress"
                >
                  <template slot-scope="props">
                    <div class="media">
                      <div class="media-content">
                        {{ props.option.description }}
                      </div>
                    </div>
                  </template>
                </b-autocomplete></b-field
              >

              <b-field>
                <b-input
                  tabindex="4"
                  type="text"
                  :placeholder="$t('user.profile.address2')"
                  autocomplete="on"
                  name="address"
                  v-model="profile.address2"
                />
              </b-field>

              <b-field>
                <b-input
                  type="text"
                  tabindex="5"
                  autocomplete="on"
                  :placeholder="$t('user.profile.zipCode')"
                  v-model="profile.zip"
                />

                <b-input
                  type="text"
                  tabindex="6"
                  :placeholder="$t('user.profile.city')"
                  autocomplete="on"
                  v-model="profile.city"
                  expanded
                />
              </b-field>
              <b-field>
                <div class="control is-expanded">
                  <span class="select is-fullwidth">
                    <country-select
                      v-model="profile.countryCode"
                      :country="profile.countryCode"
                      topCountry="NO"
                      className=""
                    />
                  </span>
                </div>
              </b-field>

              <div class="columns">
                <div class="column is-full">
                  <div>
                    <ValidationObserver ref="observer">
                      <ValidationProvider
                        :rules="
                          `required|email|sameEmail:${verificationSent},${user.emailVerified},${user.email}`
                        "
                        vid="email"
                        name="Email"
                        v-slot="{ errors }"
                      >
                        <b-field
                          :label="$t('user.profile.email')"
                          style="margin-bottom: 0.75rem;"
                          :message="errors"
                        >
                          <b-input
                            autocomplete="no"
                            name="email"
                            class="testclass"
                            v-model="profile.email"
                          ></b-input>
                        </b-field>
                      </ValidationProvider>
                      <p
                        class="help input-help is-danger"
                        v-if="
                          !verificationSent &&
                            !user.emailVerified &&
                            profile.email == user.email
                        "
                      >
                        {{ $t("user.profile.emailNotVerified") }}
                        <a
                          href="#"
                          ref="verificationButton"
                          @click="requestEmailVerification"
                          >{{ $t("user.profile.verifyEmail") }}</a
                        >
                      </p>
                      <p
                        class="help input-help"
                        :class="emailFieldCheck(user).type"
                        v-if="emailFieldCheck(user).message"
                        v-html="emailFieldCheck(user).message"
                      ></p>
                    </ValidationObserver>

                    <b-field :label="$t('user.profile.phoneNumber')">
                      <vue-tel-input
                        v-model="profile.phoneNumber"
                      ></vue-tel-input>
                    </b-field>
                  </div>

                  <p class="help mt-5 mb-5">
                    <a
                      href="#"
                      @click="
                        changePassword = !changePassword;
                        clearPasswords();
                      "
                      v-html="
                        changePassword
                          ? $t('user.profile.dontChangeMyPassword')
                          : $t('user.profile.changeMyPassword')
                      "
                    ></a>
                  </p>

                  <span v-if="changePassword">
                    <ValidationProvider
                      rules="required"
                      vid="password"
                      name="Password"
                      v-slot="{ errors, valid }"
                    >
                      <b-field
                        :label="$t('user.profile.newPassword')"
                        :type="{
                          'is-danger': errors[0],
                          'is-success': valid
                        }"
                        :message="errors"
                      >
                        <b-input
                          :input-options="{}"
                          type="password"
                          v-model="security.password"
                        ></b-input>
                      </b-field>
                    </ValidationProvider>

                    <ValidationProvider
                      rules="required|confirmed:password"
                      name="Password Confirmation"
                      v-slot="{ errors, valid }"
                    >
                      <b-field
                        :label="$t('user.profile.newPasswordRepeat')"
                        :type="{
                          'is-danger': errors[0],
                          'is-success': valid
                        }"
                        :message="errors"
                      >
                        <b-input
                          type="password"
                          v-model="security.confirmPassword"
                        ></b-input>
                      </b-field>
                    </ValidationProvider>
                  </span>

                  <div
                    class="mt-5"
                    v-if="
                      (profile.email !== user.email &&
                        !security.password &&
                        !security.confirmPassword) ||
                        (security.password &&
                          security.password === security.confirmPassword)
                    "
                  >
                    <ValidationProvider
                      rules="required"
                      name="Nåværende passord"
                      v-slot="{ errors, valid }"
                    >
                      <b-field
                        :label="$t('user.profile.currentPasswordPlaceholder')"
                        :type="{
                          'is-danger': errors[0],
                          'is-success': valid
                        }"
                        :message="errors"
                      >
                        <b-input
                          type="password"
                          v-model="security.currentPassword"
                        />
                      </b-field>
                    </ValidationProvider>

                    <b-message
                      type="is-danger"
                      v-if="security.error"
                      v-html="security.error"
                    ></b-message>
                  </div>
                </div>
              </div>

              <div class="has-text-right">
                <b-button
                  native-type="submit"
                  tabindex="6"
                  :loading="isLoading"
                  @click="handleSubmit(updateProfile)"
                  class="button is-primary"
                  :disabled="!valid"
                >
                  {{ $t("user.profile.updateProfile") }}
                </b-button>
              </div>
            </ValidationObserver>
          </div>
        </div>
        <div class="is-divider-vertical is-hidden-mobile"></div>
        <div class="column">
          <header>
            <div
              class="is-divider"
              :data-content="$t('user.profile.myCompanies')"
              v-if="profile.companies"
            ></div>
          </header>

          <div class="">
            <div class="card-content">
              <aside class="menu">
                <ul class="shortcuts">
                  <li
                    v-for="company in profile.companies"
                    :key="company.companyId"
                  >
                    <b-button
                      tag="router-link"
                      :to="{
                        name: 'EditCompany',
                        params: { companyId: company.companyId }
                      }"
                      icon-pack="fas"
                      icon-left="folder"
                      type="is-text"
                    >
                      {{ company.company.name }}
                    </b-button>
                  </li>
                </ul>
              </aside>
              <b-button
                class="mt-5"
                tag="router-link"
                :to="{ name: 'NewCompany' }"
                icon-pack="fas"
                icon-left="hammer"
                type=""
              >
                {{ $t("user.profile.addCompany") }}
              </b-button>
            </div>
          </div>

          <header>
            <div
              class="is-divider"
              :data-content="$t('user.profile.section.shortcuts')"
            ></div>
          </header>
          <div class="">
            <div class="card-content">
              <aside class="menu">
                <ul class="shortcuts">
                  <li>
                    <b-button
                      tag="router-link"
                      :to="{ name: 'MyBids' }"
                      icon-pack="fas"
                      icon-left="hammer"
                      type="is-text"
                    >
                      {{ $t("user.profile.myBids") }}
                    </b-button>
                  </li>
                  <li>
                    <b-button
                      tag="router-link"
                      :to="{ name: 'MySettings' }"
                      icon-pack="fas"
                      icon-left="hammer"
                      type="is-text"
                    >
                      {{ $t("user.profile.mySettings") }}
                      <b-tag
                        v-if="new Date() < new Date('2024-12-01')"
                        type="is-warning"
                        >{{ $t("user.profile.new") }}</b-tag
                      >
                    </b-button>
                  </li>
                  <!--<li>
                    <b-button
                      tag="router-link"
                      :to="{ name: 'MyFavorites' }"
                      icon-pack="fas"
                      icon-left="car"
                      type="is-text"
                    >
                      Mine favoritter
                    </b-button>
                  </li>-->
                  <li>
                    <b-button
                      tag="router-link"
                      :to="{ name: 'MyFilters' }"
                      icon-pack="fas"
                      icon-left="save"
                      type="is-text"
                    >
                      {{ $t("user.profile.savedSearches") }}
                    </b-button>
                  </li>

                  <li class="mt-5">
                    <router-link :to="{ path: '/info/kundeservice' }">
                      <b-button
                        tag="a"
                        icon-pack="fas"
                        icon-left="question-circle"
                        type="is-text"
                      >
                        {{ $t("user.profile.customerService") }}
                      </b-button>
                    </router-link>
                  </li>
                </ul>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </section>
    <b-modal
      v-model="isLoginModalActive"
      has-modal-card
      trap-focus
      :full-screen="isMobile()"
      :can-cancel="false"
    >
      <Login @close="closeLoginModal"></Login>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import modalMixin from "../../mixins/modal";
import autocompleteAddressMixin from "../../mixins/autocomplete-address";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import isMobile from "mobile-device-detect";
import Login from "../user/Login";
import helpers from "../../helpers";

export default {
  mixins: [modalMixin, helpers, autocompleteAddressMixin],
  components: {
    ValidationObserver,
    ValidationProvider,
    Login
  },
  data() {
    return {
      isLoadingAuth: false,
      isLoading: false,
      isLoginModalActive: false,
      changePassword: false,
      verificationSent: false,
      user: {},
      profile: {
        email: null,
        phoneNumber: null,
        displayName: null,
        address1: "",
        address2: "",
        zip: "",
        city: "",
        countryCode: "",
        subscriptions: {},
        settings: {}
      },
      security: {
        currentPassword: null,
        password: null,
        confirmPassword: null,
        error: null,
        mobileVerificationCode: null,
        mobileVerificationSent: false,
        mobileData: null
      },
      placesOptions: {
        appId: "plUO5D8CKOCV",
        apiKey: "ecdd52ef176e16caca5ba9d67d04058b",
        type: "address",
        language: "no",
        templates: {
          value: function(suggestion) {
            return suggestion.name;
          }
        },
        countries: ["NO", "SE", "DK", "SE", "FI"]
      }
    };
  },

  computed: {
    ...mapGetters([
      "getUser",
      "getUserDocument",
      "isUserAuth",
      "getLoginStatus"
    ])
  },

  methods: {
    clearPasswords() {
      this.security.password = null;
      this.security.confirmPassword = null;
      this.security.currentPassword = null;
      this.security.error = null;
      this.$refs.observer.reset();
    },

    emailFieldCheck() {
      if (
        !this.verificationSent &&
        !this.user.emailVerified &&
        this.profile.email == this.user.email
      ) {
        return {
          type: "is-warning",
          message: null
        };
      } else if (this.verificationSent) {
        return {
          type: "is-info",
          message: this.$t("user.profile.youGotAnEmail")
        };
      } else if (this.profile.email !== this.user.email) {
        return {
          type: null,
          message: null
        };
      } else {
        return {
          type: "is-success",
          message: this.$t("user.profile.emailVerified")
        };
      }
    },

    isMobile() {
      return isMobile.isMobile ? true : false;
    },

    requestEmailVerification() {
      this.$firebase.auth().currentUser.sendEmailVerification();
      this.verificationSent = true;
    },

    apiCall() {
      this.$axios
        .post(
          `${window.apiURL}/profile/update`,
          { data: this.profile },
          this.$axios.config
        )
        .then(() => {
          this.$buefy.toast.open({
            message: this.$t("user.profile.profileUpdated"),
            type: "is-success"
          });
          this.isLoading = false;
        });
    },

    updateProfile() {
      this.isLoading = true;

      // check if security should be updated
      if (
        this.profile.email !== this.user.email ||
        (this.security.password &&
          this.security.password === this.security.confirmPassword)
      ) {
        //console.log("security changed!");
        this.updateSecurity();
      } else {
        this.apiCall();
      }
    },

    updateSecurity() {
      this.isLoading = true;

      // reauth user with currentPassword provided
      var user = this.$firebase.auth().currentUser;
      var credential = this.$firebase.auth.EmailAuthProvider.credential(
        this.$firebase.auth().currentUser.email,
        this.security.currentPassword
      );

      user
        .reauthenticateWithCredential(credential)
        .then(() => {
          // user re-authenticated.
          // lets do shit

          if (this.profile.email !== this.user.email) {
            user
              .updateEmail(this.profile.email)
              .then(() => {
                this.apiCall();

                this.$buefy.toast.open({
                  message: this.$t("user.profile.emailUpdated"),
                  type: "is-success"
                });

                this.changePassword = false;
                this.clearPasswords();
              })
              .catch(error => {
                this.$buefy.toast.open({
                  message: this.firebaseMessage(error),
                  type: "is-danger"
                });
              });
          }

          if (
            this.security.password &&
            this.security.password === this.security.confirmPassword
          ) {
            user
              .updatePassword(this.security.password)
              .then(() => {
                this.$buefy.toast.open({
                  message: this.$t("user.profile.passwordUpdated"),
                  type: "is-success"
                });

                this.changePassword = false;
                this.clearPasswords();
              })
              .catch(error => {
                this.$buefy.toast.open({
                  message: error.message,
                  type: "is-danger"
                });
              });
          }

          this.isLoading = false;
        })
        .catch(error => {
          this.$buefy.toast.open({
            message: error.message,
            type: "is-danger"
          });

          // extra error message in form
          this.security.error = error.message;

          this.isLoading = false;
        });
    },

    showLoginModal() {
      if (this.isUserAuth == false) {
        this.isLoginModalActive = true;
      }
    },

    closeLoginModal() {
      this.isLoginModalActive = false;
    },

    scrollToVerification() {
      this.$refs.userVerification.scrollIntoView();
    }
  },
  created() {
    switch (this.getLoginStatus) {
      case "loggingIn":
        this.isLoadingAuth = true;
        break;
      case "loggedIn":
        this.isLoadingAuth = false;
        this.user = this.getUser;
        this.profile.phoneNumber = "";
        this.profile = this.getUserDocument;
        break;
      case "loggedOut":
        this.isLoadingAuth = false;
        this.showLoginModal();
        break;
      default:
        break;
    }
  },

  watch: {
    getLoginStatus(newValue) {
      switch (newValue) {
        case "loggingIn":
          this.isLoadingAuth = true;
          break;
        case "loggedIn":
          // Handling done in getUserDocument-watcher
          break;
        case "loggedOut":
          this.isLoadingAuth = false;
          this.showLoginModal();
          break;
        default:
          break;
      }
    },
    getUserDocument(newValue) {
      if (newValue) {
        this.user = this.getUser;
        this.profile = newValue;
      } else {
        this.profile = {
          email: null,
          phoneNumber: null,
          displayName: null,
          address1: "",
          address2: "",
          zip: "",
          city: "",
          countryCode: "",
          subscriptions: {}
        };
      }
      this.isLoadingAuth = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.shortcuts {
  li {
    a {
      text-align: left;
      text-decoration: none;
      /*&:hover {
        text-decoration: underline;
      }*/
    }
  }
}
.card-header-title span.icon {
  margin-right: 5px;
}
.input-help {
  margin: -0.5em 0em 1em;

  a {
    display: block;
    text-decoration: underline;
    font-weight: bold;
  }
}

.prospect {
  @media screen and (max-width: $tablet) {
    display: flex;
    > :nth-child(1) {
      order: 2;
    }

    > :nth-child(3) {
      order: 1;
    }
  }
}

/* clears the ‘X’ from Internet Explorer */
input[type="search"]::-ms-clear {
  display: none;
  width: 0;
  height: 0;
}
input[type="search"]::-ms-reveal {
  display: none;
  width: 0;
  height: 0;
}
/* clears the ‘X’ from Chrome */
input[type="search"]::-webkit-search-decoration,
input[type="search"]::-webkit-search-cancel-button,
input[type="search"]::-webkit-search-results-button,
input[type="search"]::-webkit-search-results-decoration {
  display: none;
}
</style>
